import React, { useState, useEffect } from 'react';
import { LMSSync } from '../../types/lms';
import { RefreshCw, CheckCircle, XCircle, AlertCircle, Play } from 'lucide-react';
import { LMSService } from '../../services/LMSService';
import { useSchool } from '../../contexts/SchoolContext';
import toast from 'react-hot-toast';

export default function LMSSyncStatus() {
  const { school } = useSchool();
  const [syncs, setSyncs] = useState<LMSSync[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!school?.id) return;

    const loadSyncHistory = async () => {
      try {
        const syncHistory = await LMSService.getSyncHistory(school.id);
        setSyncs(syncHistory);
        setLoading(false);
      } catch (error) {
        console.error('Error loading sync history:', error);
        toast.error('Failed to load sync history');
        setLoading(false);
      }
    };

    loadSyncHistory();

    // Watch for real-time sync status updates
    const unsubscribe = LMSService.watchSyncStatus(school.id, (activeSyncs) => {
      setSyncs(prev => {
        const nonActiveSyncs = prev.filter(sync => 
          !['pending', 'in-progress'].includes(sync.status)
        );
        return [...activeSyncs, ...nonActiveSyncs];
      });
    });

    return () => unsubscribe();
  }, [school?.id]);

  const handleManualSync = async (type: 'attendance' | 'grades' | 'discipline') => {
    if (!school?.id) return;

    try {
      setSyncing(true);
      await LMSService.syncAttendance(school.id);
      toast.success('Sync completed successfully');
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Sync failed: ' + (error as Error).message);
    } finally {
      setSyncing(false);
    }
  };

  const getStatusIcon = (status: LMSSync['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-success-DEFAULT" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-error-DEFAULT" />;
      case 'in-progress':
        return <RefreshCw className="w-5 h-5 text-primary-500 animate-spin" />;
      default:
        return <AlertCircle className="w-5 h-5 text-warning-DEFAULT" />;
    }
  };

  if (loading) {
    return (
      <div className="p-4 bg-surface-light rounded-lg">
        <p className="text-text-secondary">Loading sync status...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">LMS Synchronization Status</h2>
        <button
          onClick={() => handleManualSync('attendance')}
          disabled={syncing || loading}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play className="h-4 w-4 mr-2" />
          {syncing ? 'Syncing...' : 'Manual Sync'}
        </button>
      </div>

      <div className="space-y-3">
        {syncs.map((sync) => (
          <div
            key={sync.id}
            className={`p-4 rounded-lg border ${
              sync.status === 'failed'
                ? 'border-red-200 bg-red-50'
                : sync.status === 'completed'
                ? 'border-green-200 bg-green-50'
                : sync.status === 'in-progress'
                ? 'border-blue-200 bg-blue-50'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {getStatusIcon(sync.status)}
                <h3 className="font-medium text-gray-900 capitalize">
                  {sync.type} Sync
                </h3>
              </div>
              <span className="text-sm text-gray-500">
                {new Date(sync.startedAt).toLocaleTimeString()}
              </span>
            </div>

            {sync.details && (
              <div className="text-sm text-gray-600 space-y-1">
                <p>Processed: {sync.details.recordsProcessed}</p>
                <p>Updated: {sync.details.recordsUpdated}</p>
                {sync.details.warnings.length > 0 && (
                  <div className="mt-2 text-yellow-700">
                    <p>Warnings:</p>
                    <ul className="list-disc list-inside">
                      {sync.details.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {sync.error && (
              <p className="mt-2 text-sm text-red-600">{sync.error}</p>
            )}
          </div>
        ))}

        {syncs.length === 0 && (
          <p className="text-gray-500 text-center py-8">No recent synchronizations</p>
        )}
      </div>
    </div>
  );
}